// src/components/ledger/PettyCash.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { db } from '../firebase/config';
import { 
  collection, getDocs, addDoc, updateDoc, deleteDoc, doc, 
  query, orderBy, Timestamp, where
} from 'firebase/firestore';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Expense Categories for Furniture Company
const EXPENSE_CATEGORIES = {
  transport: { label: '🚛 Transport', icon: '🚛', color: '#3b82f6' },
  fuel: { label: '⛽ Fuel - Generator', icon: '⛽', color: '#f59e0b' },
  electricity: { label: '💡 Electricity', icon: '💡', color: '#fbbf24' },
  materials: { label: '🪵 Materials / Raw Materials', icon: '🪵', color: '#10b981' },
  tools: { label: '🔧 Tools & Equipment', icon: '🔧', color: '#6366f1' },
  staff_meals: { label: '🍽️ Staff Meals', icon: '🍽️', color: '#ef4444' },
  staff_advance: { label: '💰 Staff Advance', icon: '💰', color: '#8b5cf6' },
  maintenance: { label: '🔨 Maintenance & Repairs', icon: '🔨', color: '#f97316' },
  cleaning: { label: '🧹 Cleaning Supplies', icon: '🧹', color: '#0ea5e9' },
  office_supplies: { label: '📎 Office Supplies', icon: '📎', color: '#14b8a6' },
  water: { label: '💧 Water Bills', icon: '💧', color: '#06b6d4' },
  internet: { label: '🌐 Internet', icon: '🌐', color: '#8b5cf6' },
  airtime: { label: '📱 Airtime / Communication', icon: '📱', color: '#ec4899' },
  tea: { label: '☕ Tea / Refreshments', icon: '☕', color: '#d97706' },
  waste: { label: '🗑️ Waste Disposal', icon: '🗑️', color: '#6b7280' },
  security: { label: '👮 Security', icon: '👮', color: '#4f46e5' },
  charges: { label: '💸 Transaction Charges', icon: '💸', color: '#f43f5e' },
  other: { label: '📋 Other', icon: '📋', color: '#94a3b8' }
};

export const PettyCash = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [summaryPeriod, setSummaryPeriod] = useState('week');
  const [summaryData, setSummaryData] = useState({});

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const transactionsRef = collection(db, 'pettyCash');
      const snapshot = await getDocs(query(transactionsRef, orderBy('transactionDate', 'desc')));
      const transactionsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        transactionDate: doc.data().transactionDate?.toDate?.() || new Date(doc.data().date || doc.data().createdAt?.toDate?.())
      }));
      setTransactions(transactionsData);
      calculateSummary(transactionsData, 'week');
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateSummary = (data, period) => {
    const now = new Date();
    let filtered = [...data];
    if (period === 'week') {
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() - now.getDay());
      weekStart.setHours(0, 0, 0, 0);
      filtered = data.filter(t => t.transactionDate >= weekStart);
    } else if (period === 'month') {
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      filtered = data.filter(t => t.transactionDate >= monthStart);
    } else if (period === 'year') {
      const yearStart = new Date(now.getFullYear(), 0, 1);
      filtered = data.filter(t => t.transactionDate >= yearStart);
    }
    const summary = {};
    Object.keys(EXPENSE_CATEGORIES).forEach(cat => {
      if (cat === 'charges') {
        summary[cat] = filtered.reduce((sum, t) => sum + (t.charge || 0), 0);
      } else {
        summary[cat] = filtered
          .filter(t => t.category === cat)
          .reduce((sum, t) => sum + (t.amount || 0), 0);
      }
    });
    summary.total = filtered.reduce((sum, t) => sum + (t.amount || 0) + (t.charge || 0), 0);
    setSummaryData(summary);
  };

  const getDateFilter = useCallback(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    switch (dateRange) {
      case 'today':
        return { start: today, end: tomorrow };
      case 'week': {
        const start = new Date(now);
        start.setDate(now.getDate() - now.getDay());
        start.setHours(0, 0, 0, 0);
        const end = new Date(start);
        end.setDate(start.getDate() + 7);
        return { start, end };
      }
      case 'month': {
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        return { start, end };
      }
      case 'year': {
        const start = new Date(now.getFullYear(), 0, 1);
        const end = new Date(now.getFullYear(), 11, 31);
        return { start, end };
      }
      case 'custom':
        return {
          start: customStartDate ? new Date(customStartDate) : null,
          end: customEndDate ? new Date(customEndDate) : null
        };
      default:
        return null;
    }
  }, [dateRange, customStartDate, customEndDate]);

  const getFilteredTransactions = useMemo(() => {
    let filtered = [...transactions];

    // Special handling for 'charges' category – show any transaction with charge > 0
    if (selectedCategory === 'charges') {
      filtered = filtered.filter(t => (t.charge || 0) > 0);
    } else if (selectedCategory !== 'all') {
      filtered = filtered.filter(t => t.category === selectedCategory);
    }

    const dateFilter = getDateFilter();
    if (dateFilter) {
      if (dateFilter.start) {
        filtered = filtered.filter(t => t.transactionDate >= dateFilter.start);
      }
      if (dateFilter.end) {
        filtered = filtered.filter(t => t.transactionDate < dateFilter.end);
      }
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(t =>
        t.description?.toLowerCase().includes(term) ||
        t.payee?.toLowerCase().includes(term) ||
        t.category?.toLowerCase().includes(term)
      );
    }

    return filtered;
  }, [transactions, selectedCategory, getDateFilter, searchTerm]);

  const getPaginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return getFilteredTransactions.slice(startIndex, startIndex + itemsPerPage);
  }, [getFilteredTransactions, currentPage, itemsPerPage]);

  const getTotalPages = useMemo(() => Math.ceil(getFilteredTransactions.length / itemsPerPage), [getFilteredTransactions]);

  const getCategoryTotals = useMemo(() => {
    const totals = {};
    Object.keys(EXPENSE_CATEGORIES).forEach(cat => {
      if (cat === 'charges') {
        totals[cat] = getFilteredTransactions.reduce((sum, t) => sum + (t.charge || 0), 0);
      } else {
        totals[cat] = getFilteredTransactions
          .filter(t => t.category === cat)
          .reduce((sum, t) => sum + (t.amount || 0), 0);
      }
    });
    totals.total = getFilteredTransactions.reduce((sum, t) => sum + (t.amount || 0) + (t.charge || 0), 0);
    return totals;
  }, [getFilteredTransactions]);

  // Helper to get total charges (sum of all charge fields)
  const getTotalCharges = useMemo(() => {
    return getFilteredTransactions.reduce((sum, t) => sum + (t.charge || 0), 0);
  }, [getFilteredTransactions]);

  const handleAddTransaction = async (formData) => {
    try {
      const amountValue = parseFloat(formData.amount) || 0;
      const chargeValue = parseFloat(formData.charge) || 0;
      const totalOutflow = amountValue + chargeValue;

      if (amountValue <= 0 && chargeValue <= 0) {
        alert('Please enter a valid amount or charge');
        return;
      }

      const transactionData = {
        category: formData.category,
        payee: formData.payee || '',
        description: formData.description,
        amount: amountValue,
        charge: chargeValue,
        totalAmount: totalOutflow,
        transactionDate: Timestamp.fromDate(new Date(formData.transactionDate)),
        reference: formData.reference || `PETTY-${Date.now()}`,
        createdBy: 'system',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      const docRef = await addDoc(collection(db, 'pettyCash'), transactionData);

      // Journal entry: split between expense and charges
      const journalEntries = [
        {
          account: `expense_${formData.category}`,
          accountName: EXPENSE_CATEGORIES[formData.category]?.label || formData.category,
          debit: amountValue,
          credit: 0,
          description: formData.description
        }
      ];
      if (chargeValue > 0) {
        journalEntries.push({
          account: 'expense_charges',
          accountName: 'Transaction Charges',
          debit: chargeValue,
          credit: 0,
          description: `Charges for ${formData.description}`
        });
      }
      journalEntries.push({
        account: 'cash',
        accountName: 'Cash Account',
        debit: 0,
        credit: totalOutflow,
        description: `Petty cash payment (${formData.description}) incl. charges`
      });

      const journalData = {
        journalId: `JC-${Date.now()}-${docRef.id.slice(-6)}`,
        transactionId: docRef.id,
        transactionType: 'petty_cash_expense',
        date: Timestamp.now(),
        entries: journalEntries,
        createdAt: Timestamp.now(),
        createdBy: 'system'
      };

      await addDoc(collection(db, 'journalEntries'), journalData);

      await fetchTransactions();
      setShowModal(false);
      alert('Transaction added successfully!');
    } catch (error) {
      console.error('Error adding transaction:', error);
      alert('Error adding transaction: ' + error.message);
    }
  };

  const handleUpdateTransaction = async (id, formData) => {
    try {
      const amountValue = parseFloat(formData.amount) || 0;
      const chargeValue = parseFloat(formData.charge) || 0;
      const totalOutflow = amountValue + chargeValue;

      const transactionRef = doc(db, 'pettyCash', id);
      const updateData = {
        category: formData.category,
        payee: formData.payee || '',
        description: formData.description,
        amount: amountValue,
        charge: chargeValue,
        totalAmount: totalOutflow,
        transactionDate: Timestamp.fromDate(new Date(formData.transactionDate)),
        reference: formData.reference,
        updatedAt: Timestamp.now()
      };

      await updateDoc(transactionRef, updateData);

      // Update journal entry
      const journalQuery = query(
        collection(db, 'journalEntries'),
        where('transactionId', '==', id),
        where('transactionType', '==', 'petty_cash_expense')
      );
      const journalSnapshot = await getDocs(journalQuery);

      if (!journalSnapshot.empty) {
        const journalDoc = journalSnapshot.docs[0];
        const journalRef = doc(db, 'journalEntries', journalDoc.id);

        const journalEntries = [
          {
            account: `expense_${formData.category}`,
            accountName: EXPENSE_CATEGORIES[formData.category]?.label || formData.category,
            debit: amountValue,
            credit: 0,
            description: formData.description
          }
        ];
        if (chargeValue > 0) {
          journalEntries.push({
            account: 'expense_charges',
            accountName: 'Transaction Charges',
            debit: chargeValue,
            credit: 0,
            description: `Charges for ${formData.description}`
          });
        }
        journalEntries.push({
          account: 'cash',
          accountName: 'Cash Account',
          debit: 0,
          credit: totalOutflow,
          description: `Petty cash payment (${formData.description}) incl. charges`
        });

        await updateDoc(journalRef, {
          entries: journalEntries,
          updatedAt: Timestamp.now()
        });
      }

      await fetchTransactions();
      setEditingItem(null);
      alert('Transaction updated successfully!');
    } catch (error) {
      console.error('Error updating transaction:', error);
      alert('Error updating transaction: ' + error.message);
    }
  };

  const handleDeleteTransaction = async (id) => {
    if (!window.confirm('Are you sure you want to delete this transaction?')) return;
    try {
      await deleteDoc(doc(db, 'pettyCash', id));
      await fetchTransactions();
      alert('Transaction deleted successfully!');
    } catch (error) {
      console.error('Error deleting transaction:', error);
      alert('Error deleting transaction: ' + error.message);
    }
  };

  const generateReport = () => {
    const doc = new jsPDF('landscape');
    const pageWidth = doc.internal.pageSize.width;
    const filtered = getFilteredTransactions;
    const totals = getCategoryTotals;

    doc.setFillColor(127, 26, 46);
    doc.rect(0, 0, pageWidth, 45, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.text('PETTY CASH REPORT', pageWidth / 2, 28, { align: 'center' });

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 20, 60);
    doc.text(`Period: ${dateRange === 'today' ? 'Today' : dateRange === 'week' ? 'This Week' : dateRange === 'month' ? 'This Month' : dateRange === 'year' ? 'This Year' : 'All Time'}`, 20, 68);

    let y = 80;
    doc.setFillColor(240, 240, 240);
    doc.rect(20, y, pageWidth - 40, 12, 'F');
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('SUMMARY BY CATEGORY', 25, y + 8);
    y += 18;

    const categoryRows = Object.entries(totals)
      .filter(([cat, amount]) => cat !== 'total' && amount > 0)
      .map(([cat, amount]) => [
        EXPENSE_CATEGORIES[cat]?.label || cat,
        amount.toLocaleString(),
        `${((amount / totals.total) * 100).toFixed(1)}%`
      ]);

    if (categoryRows.length > 0) {
      autoTable(doc, {
        startY: y,
        head: [['Category', 'Amount (UGX)', 'Percentage']],
        body: categoryRows,
        foot: [['TOTAL', totals.total.toLocaleString(), '100%']],
        theme: 'striped',
        headStyles: { fillColor: [127, 26, 46], textColor: [255, 255, 255] },
        footStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold' },
        styles: { fontSize: 9 }
      });
      y = doc.lastAutoTable.finalY + 10;
    }

    const transactionRows = filtered.map(t => [
      t.transactionDate?.toLocaleDateString() || '-',
      EXPENSE_CATEGORIES[t.category]?.label || t.category,
      t.payee || '-',
      t.description,
      (t.amount || 0).toLocaleString(),
      (t.charge || 0).toLocaleString(),
      ((t.amount || 0) + (t.charge || 0)).toLocaleString(),
      t.reference || '-'
    ]);

    autoTable(doc, {
      startY: y,
      head: [['Date', 'Category', 'Payee', 'Description', 'Amount', 'Charge', 'Total', 'Reference']],
      body: transactionRows,
      theme: 'striped',
      headStyles: { fillColor: [127, 26, 46], textColor: [255, 255, 255] },
      styles: { fontSize: 8 }
    });

    doc.save(`Petty_Cash_Report_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p>Loading petty cash transactions...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>💰 Petty Cash Management</h1>
          <p style={styles.subtitle}>Track all cash expenses including transaction charges</p>
        </div>
        <div style={styles.headerActions}>
          <button style={styles.reportButton} onClick={generateReport}>📄 Export Report</button>
          <button style={styles.addButton} onClick={() => setShowModal(true)}>+ Add Expense</button>
        </div>
      </div>

      <div style={styles.summaryGrid}>
        <div style={styles.summaryCard}>
          <div style={styles.summaryIcon}>💰</div>
          <div>
            <div style={styles.summaryLabel}>Total Cash Out</div>
            <div style={styles.summaryValue}>{getCategoryTotals.total.toLocaleString()} UGX</div>
          </div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryIcon}>💸</div>
          <div>
            <div style={styles.summaryLabel}>Total Charges</div>
            <div style={styles.summaryValue}>{getTotalCharges.toLocaleString()} UGX</div>
          </div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryIcon}>📊</div>
          <div>
            <div style={styles.summaryLabel}>Transactions</div>
            <div style={styles.summaryValue}>{getFilteredTransactions.length}</div>
          </div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryIcon}>📁</div>
          <div>
            <div style={styles.summaryLabel}>Categories Used</div>
            <div style={styles.summaryValue}>{Object.values(getCategoryTotals).filter(v => v > 0).length - 1}</div>
          </div>
        </div>
      </div>

      <div style={styles.categorySummary}>
        <h3 style={styles.sectionTitle}>Expense Summary by Category</h3>
        <div style={styles.categoryChips}>
          {Object.entries(getCategoryTotals).filter(([cat, amount]) => cat !== 'total' && amount > 0).map(([cat, amount]) => (
            <div key={cat} style={styles.categoryChip}>
              <span style={{ fontSize: '20px' }}>{EXPENSE_CATEGORIES[cat]?.icon}</span>
              <div style={styles.categoryChipInfo}>
                <div style={styles.categoryChipLabel}>{EXPENSE_CATEGORIES[cat]?.label}</div>
                <div style={styles.categoryChipAmount}>{amount.toLocaleString()} UGX</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={styles.filtersBar}>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Category</label>
          <select style={styles.filterSelect} value={selectedCategory} onChange={(e) => { setSelectedCategory(e.target.value); setCurrentPage(1); }}>
            <option value="all">All Categories</option>
            {Object.entries(EXPENSE_CATEGORIES).map(([key, val]) => (
              <option key={key} value={key}>{val.icon} {val.label}</option>
            ))}
          </select>
        </div>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Date Range</label>
          <select style={styles.filterSelect} value={dateRange} onChange={(e) => { setDateRange(e.target.value); setCurrentPage(1); }}>
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="year">This Year</option>
            <option value="custom">Custom Range</option>
          </select>
        </div>
        {dateRange === 'custom' && (
          <>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>From</label>
              <input type="date" style={styles.filterInput} value={customStartDate} onChange={(e) => { setCustomStartDate(e.target.value); setCurrentPage(1); }} />
            </div>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>To</label>
              <input type="date" style={styles.filterInput} value={customEndDate} onChange={(e) => { setCustomEndDate(e.target.value); setCurrentPage(1); }} />
            </div>
          </>
        )}
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Search</label>
          <input type="text" style={styles.filterInput} placeholder="Search by description..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
        </div>
      </div>

      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.tableHeaderRow}>
              <th style={styles.tableHeader}>Date</th>
              <th style={styles.tableHeader}>Category</th>
              <th style={styles.tableHeader}>Payee</th>
              <th style={styles.tableHeader}>Description</th>
              <th style={styles.tableHeader}>Amount (UGX)</th>
              <th style={styles.tableHeader}>Charge (UGX)</th>
              <th style={styles.tableHeader}>Total (UGX)</th>
              <th style={styles.tableHeader}>Reference</th>
              <th style={styles.tableHeader}>Actions</th>
             </tr>
          </thead>
          <tbody>
            {getPaginatedData.map(transaction => {
              const category = EXPENSE_CATEGORIES[transaction.category] || EXPENSE_CATEGORIES.other;
              const total = (transaction.amount || 0) + (transaction.charge || 0);
              return (
                <tr key={transaction.id} style={styles.tableRow}>
                  <td style={styles.tableCell}>{transaction.transactionDate?.toLocaleDateString() || '-'}</td>
                  <td style={styles.tableCell}><span style={{ fontSize: '16px' }}>{category.icon}</span> {category.label}</td>
                  <td style={styles.tableCell}>{transaction.payee || '-'}</td>
                  <td style={styles.tableCell}>{transaction.description}</td>
                  <td style={styles.tableCell}>{transaction.amount?.toLocaleString()} UGX</td>
                  <td style={{...styles.tableCell, color: '#f43f5e'}}>{transaction.charge?.toLocaleString()} UGX</td>
                  <td style={{...styles.tableCell, fontWeight: '600'}}>{total.toLocaleString()} UGX</td>
                  <td style={styles.tableCell}>{transaction.reference || '-'}</td>
                  <td style={styles.tableCell}>
                    <div style={styles.actionButtons}>
                      <button style={styles.viewButton} onClick={() => { setSelectedTransaction(transaction); setShowDetailModal(true); }}>👁️</button>
                      <button style={styles.editButton} onClick={() => setEditingItem(transaction)}>✏️</button>
                      <button style={styles.deleteButton} onClick={() => handleDeleteTransaction(transaction.id)}>🗑️</button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {getPaginatedData.length === 0 && (
              <tr><td colSpan="9" style={styles.emptyState}>No transactions found</td></tr>
            )}
          </tbody>
          <tfoot>
            <tr style={styles.tableFooter}>
              <td colSpan="4" style={{textAlign: 'right', fontWeight: 'bold'}}>TOTAL:</td>
              <td style={{fontWeight: 'bold'}}>{getFilteredTransactions.reduce((sum, t) => sum + (t.amount || 0), 0).toLocaleString()} UGX</td>
              <td style={{fontWeight: 'bold', color: '#f43f5e'}}>{getTotalCharges.toLocaleString()} UGX</td>
              <td style={{fontWeight: 'bold', color: '#dc2626'}}>{getCategoryTotals.total.toLocaleString()} UGX</td>
              <td colSpan="2"></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {getTotalPages > 1 && (
        <div style={styles.pagination}>
          <button style={{...styles.pageButton, ...(currentPage === 1 ? styles.pageButtonDisabled : {})}} onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1}>Previous</button>
          <span style={styles.pageInfo}>Page {currentPage} of {getTotalPages}</span>
          <button style={{...styles.pageButton, ...(currentPage === getTotalPages ? styles.pageButtonDisabled : {})}} onClick={() => setCurrentPage(prev => Math.min(getTotalPages, prev + 1))} disabled={currentPage === getTotalPages}>Next</button>
        </div>
      )}

      {(showModal || editingItem) && (
        <PettyCashModal
          transaction={editingItem}
          categories={EXPENSE_CATEGORIES}
          onClose={() => { setShowModal(false); setEditingItem(null); }}
          onAdd={handleAddTransaction}
          onUpdate={handleUpdateTransaction}
        />
      )}

      {showDetailModal && selectedTransaction && (
        <DetailModal
          transaction={selectedTransaction}
          categories={EXPENSE_CATEGORIES}
          onClose={() => { setShowDetailModal(false); setSelectedTransaction(null); }}
        />
      )}
    </div>
  );
};

// Petty Cash Modal Component
const PettyCashModal = ({ transaction, categories, onClose, onAdd, onUpdate }) => {
  const [formData, setFormData] = useState({
    category: transaction?.category || 'transport',
    payee: transaction?.payee || '',
    description: transaction?.description || '',
    amount: transaction?.amount !== undefined ? transaction.amount : '',
    charge: transaction?.charge !== undefined ? transaction.charge : '',
    transactionDate: transaction?.transactionDate ? new Date(transaction.transactionDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    reference: transaction?.reference || `PETTY-${Date.now()}`
  });
  const [loading, setLoading] = useState(false);

  const totalOut = (parseFloat(formData.amount) || 0) + (parseFloat(formData.charge) || 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.description.trim()) {
      alert('Please enter a description');
      return;
    }
    const amountNum = parseFloat(formData.amount) || 0;
    const chargeNum = parseFloat(formData.charge) || 0;
    if (amountNum <= 0 && chargeNum <= 0) {
      alert('Please enter a valid amount or charge');
      return;
    }
    setLoading(true);
    try {
      if (transaction && transaction.id) {
        await onUpdate(transaction.id, formData);
      } else {
        await onAdd(formData);
      }
      onClose();
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={modalStyles.container} onClick={(e) => e.stopPropagation()}>
        <div style={modalStyles.header}>
          <h2 style={modalStyles.title}>{transaction ? 'Edit Expense' : 'New Petty Cash Expense'}</h2>
          <button style={modalStyles.closeButton} onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit} style={modalStyles.form}>
          <div style={modalStyles.formGroup}>
            <label style={modalStyles.label}>Expense Category *</label>
            <select style={modalStyles.select} value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})} required>
              {Object.entries(categories).map(([key, val]) => <option key={key} value={key}>{val.icon} {val.label}</option>)}
            </select>
          </div>
          <div style={modalStyles.formRow}>
            <div style={{ flex: 1 }}>
              <label style={modalStyles.label}>Date *</label>
              <input type="date" style={modalStyles.input} value={formData.transactionDate} onChange={(e) => setFormData({...formData, transactionDate: e.target.value})} required />
            </div>
            <div style={{ flex: 1 }}>
              <label style={modalStyles.label}>Amount (UGX)</label>
              <input type="number" style={modalStyles.input} value={formData.amount} onChange={(e) => setFormData({...formData, amount: e.target.value})} placeholder="0" step="500" />
            </div>
          </div>
          <div style={modalStyles.formRow}>
            <div style={{ flex: 1 }}>
              <label style={modalStyles.label}>Charge (UGX)</label>
              <input type="number" style={{...modalStyles.input, background: '#fef3c7'}} value={formData.charge} onChange={(e) => setFormData({...formData, charge: e.target.value})} placeholder="0" step="100" />
              <small style={{ fontSize: '10px', color: '#f43f5e' }}>Additional charges (e.g., bank fee, mobile money fee)</small>
            </div>
            <div style={{ flex: 1 }}>
              <label style={modalStyles.label}>Total Cash Out</label>
              <input type="text" style={{...modalStyles.input, background: '#eef2ff', fontWeight: 'bold'}} value={totalOut.toLocaleString()} readOnly />
            </div>
          </div>
          <div style={modalStyles.formRow}>
            <div style={{ flex: 1 }}>
              <label style={modalStyles.label}>Payee (Optional)</label>
              <input type="text" style={modalStyles.input} value={formData.payee} onChange={(e) => setFormData({...formData, payee: e.target.value})} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={modalStyles.label}>Reference</label>
              <input type="text" style={modalStyles.input} value={formData.reference} onChange={(e) => setFormData({...formData, reference: e.target.value})} />
            </div>
          </div>
          <div style={modalStyles.formGroup}>
            <label style={modalStyles.label}>Description / Purpose *</label>
            <textarea style={{...modalStyles.input, ...modalStyles.textarea}} value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows="3" required />
          </div>
          <div style={modalStyles.actions}>
            <button type="button" style={modalStyles.buttonSecondary} onClick={onClose}>Cancel</button>
            <button type="submit" style={modalStyles.buttonPrimary} disabled={loading}>{loading ? 'Saving...' : transaction ? 'Update' : 'Add Expense'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Detail Modal Component
const DetailModal = ({ transaction, categories, onClose }) => {
  const category = categories[transaction.category] || categories.other;
  const total = (transaction.amount || 0) + (transaction.charge || 0);
  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={{...modalStyles.container, maxWidth: '500px'}} onClick={(e) => e.stopPropagation()}>
        <div style={modalStyles.header}>
          <h2 style={modalStyles.title}>Transaction Details</h2>
          <button style={modalStyles.closeButton} onClick={onClose}>×</button>
        </div>
        <div style={modalStyles.content}>
          <div style={detailStyles.detailRow}><span style={detailStyles.label}>Date:</span><span style={detailStyles.value}>{transaction.transactionDate?.toLocaleDateString()}</span></div>
          <div style={detailStyles.detailRow}><span style={detailStyles.label}>Category:</span><span style={detailStyles.value}>{category.icon} {category.label}</span></div>
          <div style={detailStyles.detailRow}><span style={detailStyles.label}>Amount:</span><span style={detailStyles.value}>{transaction.amount?.toLocaleString()} UGX</span></div>
          <div style={detailStyles.detailRow}><span style={detailStyles.label}>Charge:</span><span style={{...detailStyles.value, color: '#f43f5e'}}>{transaction.charge?.toLocaleString()} UGX</span></div>
          <div style={detailStyles.detailRow}><span style={detailStyles.label}>Total Cash Out:</span><span style={{...detailStyles.value, fontWeight: 'bold'}}>{total.toLocaleString()} UGX</span></div>
          <div style={detailStyles.detailRow}><span style={detailStyles.label}>Payee:</span><span style={detailStyles.value}>{transaction.payee || 'N/A'}</span></div>
          <div style={detailStyles.detailRow}><span style={detailStyles.label}>Reference:</span><span style={detailStyles.value}>{transaction.reference || 'N/A'}</span></div>
          <div style={detailStyles.detailRow}><span style={detailStyles.label}>Description:</span><span style={detailStyles.value}>{transaction.description}</span></div>
          <div style={detailStyles.detailRow}><span style={detailStyles.label}>Created:</span><span style={detailStyles.value}>{transaction.createdAt?.toDate?.()?.toLocaleString() || 'N/A'}</span></div>
        </div>
        <div style={modalStyles.footer}><button style={modalStyles.closeButtonFooter} onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
};

// ==================== STYLES ====================
const styles = {
  container: { padding: '28px', background: '#f8fafc', minHeight: '100vh' },
  loadingContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px', color: '#64748b' },
  spinner: { width: '40px', height: '40px', border: '3px solid #e2e8f0', borderTopColor: '#7f1a2e', borderRadius: '50%', animation: 'spin 1s linear infinite', marginBottom: '16px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px', flexWrap: 'wrap', gap: '16px' },
  title: { fontSize: '24px', fontWeight: '700', color: '#0f172a', marginBottom: '4px' },
  subtitle: { fontSize: '13px', color: '#64748b' },
  headerActions: { display: 'flex', gap: '12px', flexWrap: 'wrap' },
  addButton: { background: '#7f1a2e', color: '#ffffff', padding: '10px 20px', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
  reportButton: { background: '#f1f5f9', color: '#1e293b', padding: '10px 20px', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
  summaryGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '28px' },
  summaryCard: { background: '#ffffff', padding: '20px', borderRadius: '16px', display: 'flex', alignItems: 'center', gap: '16px', border: '1px solid #eef2f6' },
  summaryIcon: { fontSize: '32px' },
  summaryLabel: { fontSize: '11px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#64748b', marginBottom: '4px' },
  summaryValue: { fontSize: '20px', fontWeight: '700', color: '#0f172a' },
  categorySummary: { background: '#ffffff', padding: '20px', borderRadius: '16px', marginBottom: '24px', border: '1px solid #eef2f6' },
  sectionTitle: { fontSize: '14px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' },
  categoryChips: { display: 'flex', flexWrap: 'wrap', gap: '12px' },
  categoryChip: { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 16px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #eef2f6', minWidth: '180px' },
  categoryChipInfo: { flex: 1 },
  categoryChipLabel: { fontSize: '12px', fontWeight: '500', color: '#475569' },
  categoryChipAmount: { fontSize: '14px', fontWeight: '600', color: '#dc2626' },
  filtersBar: { display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap', alignItems: 'flex-end', background: '#ffffff', padding: '16px', borderRadius: '12px', border: '1px solid #eef2f6' },
  filterGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
  filterLabel: { fontSize: '12px', fontWeight: '500', color: '#475569' },
  filterSelect: { padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', background: '#ffffff', cursor: 'pointer' },
  filterInput: { padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', background: '#ffffff', outline: 'none' },
  tableWrapper: { overflowX: 'auto', borderRadius: '12px', border: '1px solid #eef2f6', background: '#ffffff', marginBottom: '24px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  tableHeaderRow: { background: '#f8fafc', borderBottom: '1px solid #eef2f6' },
  tableHeader: { textAlign: 'left', padding: '14px 16px', fontWeight: '600', color: '#475569' },
  tableRow: { borderBottom: '1px solid #f1f5f9' },
  tableCell: { padding: '12px 16px', color: '#334155' },
  tableFooter: { background: '#f8fafc', borderTop: '2px solid #eef2f6' },
  actionButtons: { display: 'flex', gap: '8px' },
  viewButton: { background: '#8b5cf6', color: '#ffffff', padding: '6px 10px', border: 'none', borderRadius: '6px', fontSize: '14px', cursor: 'pointer' },
  editButton: { background: '#3b82f6', color: '#ffffff', padding: '6px 10px', border: 'none', borderRadius: '6px', fontSize: '14px', cursor: 'pointer' },
  deleteButton: { background: '#ef4444', color: '#ffffff', padding: '6px 10px', border: 'none', borderRadius: '6px', fontSize: '14px', cursor: 'pointer' },
  emptyState: { textAlign: 'center', padding: '48px', color: '#94a3b8', fontSize: '13px' },
  pagination: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', marginTop: '24px', padding: '16px' },
  pageButton: { padding: '8px 16px', background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' },
  pageButtonDisabled: { opacity: 0.5, cursor: 'not-allowed' },
  pageInfo: { fontSize: '13px', color: '#64748b' }
};

const modalStyles = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  container: { background: '#ffffff', borderRadius: '24px', width: '90%', maxWidth: '600px', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #eef2f6' },
  title: { fontSize: '20px', fontWeight: '600', color: '#0f172a' },
  closeButton: { background: 'none', border: 'none', fontSize: '28px', cursor: 'pointer', color: '#94a3b8' },
  content: { padding: '24px' },
  footer: { padding: '16px 24px', borderTop: '1px solid #eef2f6', display: 'flex', justifyContent: 'flex-end' },
  closeButtonFooter: { padding: '8px 20px', background: '#f1f5f9', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: '500', cursor: 'pointer', color: '#475569' },
  form: { padding: '24px' },
  formGroup: { marginBottom: '20px' },
  formRow: { display: 'flex', gap: '16px', marginBottom: '20px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '500', color: '#334155', marginBottom: '8px' },
  input: { width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '14px', outline: 'none' },
  textarea: { resize: 'vertical', fontFamily: 'inherit' },
  select: { width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '14px', background: '#ffffff', cursor: 'pointer' },
  actions: { display: 'flex', gap: '12px', marginTop: '24px' },
  buttonPrimary: { flex: 1, background: '#7f1a2e', color: '#ffffff', padding: '12px', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  buttonSecondary: { flex: 1, background: '#f1f5f9', color: '#334155', padding: '12px', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }
};

const detailStyles = {
  detailRow: { display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f1f5f9' },
  label: { fontWeight: '600', color: '#475569', fontSize: '13px' },
  value: { color: '#1e293b', fontSize: '13px', textAlign: 'right', maxWidth: '60%', wordBreak: 'break-word' }
};

// Add animation
const styleSheet = document.createElement('style');
styleSheet.textContent = `@keyframes spin { to { transform: rotate(360deg); } }`;
document.head.appendChild(styleSheet);

export default PettyCash;